import { z } from 'zod'
import { DiditModeSchema, ElectionKeyModeSchema, ElectionStatusSchema } from '../enums'
import { ScheduleSchema } from '../domain/election-config'
import { PublicAreaSchema, publicArea } from '../domain/geo'
import { RegionIdSchema } from '../domain/regions'
import {
  acceptedCitizenships,
  DocumentTypeSchema,
  effectiveModules,
  FaceTemplateRetentionSchema,
  faceTemplateRetentionOf,
  PhoneRequirementSchema,
  type RequirementsV2,
} from '../domain/requirements'
import { ResidenceAcceptedSourcesSchema } from '../domain/residence'
import { QuestionInputSchema } from '../domain/questions'
import {
  BigIntStringSchema,
  CountryIso3Schema,
  EthAddressSchema,
  IsoDateTimeSchema,
  SlugSchema,
  UuidSchema,
} from '../primitives'
import { CouncilPolicySchema } from './councils'
import { ProcessStateSchema, ResultsResponseSchema } from './elections'

/* ================================================================== organization */

/**
 * Public-safe projection of the organization's requirements (v2, PLAN v4 R17): which modules apply, accepted
 * document types, citizenship (presets + explicit + expanded list), residence areas (labels + simplified geometry
 * for the landing map) and accepted evidence, ages, location collection. Never exposes faceMatchAutoApprove,
 * vpnPolicy or the OSM metadata. Email is always required (not a knob), so it is not included.
 */
export const PublicRequirementsSchema = z.object({
  version: z.literal(2),
  contact: z.object({ phone: PhoneRequirementSchema }),
  /** Liveness + duplicate-face check (implied by the document check). */
  uniqueHuman: z.boolean(),
  /** How long the image-free face template is kept after the verification is deleted (null without liveness). */
  faceTemplateRetention: FaceTemplateRetentionSchema.nullable(),
  identityDocument: z.object({ enabled: z.boolean(), documentTypes: z.array(DocumentTypeSchema) }),
  citizenship: z.object({
    enabled: z.boolean(),
    regions: z.array(RegionIdSchema),
    countries: z.array(CountryIso3Schema),
    /** regions ∪ countries, expanded (what the member's document nationality is checked against). */
    accepted: z.array(CountryIso3Schema),
  }),
  residence: z.object({
    enabled: z.boolean(),
    areas: z.array(PublicAreaSchema),
    evidence: ResidenceAcceptedSourcesSchema,
    proofOfAddressMaxAgeMonths: z.number().int(),
  }),
  age: z.object({ minAge: z.number().int().nullable(), maxAge: z.number().int().nullable() }),
  collectLocation: z.boolean(),
})
export type PublicRequirements = z.infer<typeof PublicRequirementsSchema>

/** The public projection of requirements (API `publicOrganization`, dev mocks). */
export function publicRequirements(r: RequirementsV2): PublicRequirements {
  const m = effectiveModules(r)
  return {
    version: 2,
    contact: { phone: r.contact.phone },
    uniqueHuman: m.uniqueHuman,
    faceTemplateRetention: m.uniqueHuman ? faceTemplateRetentionOf(r) : null,
    identityDocument: {
      enabled: r.identityDocument.enabled,
      documentTypes: r.identityDocument.enabled ? [...r.identityDocument.documentTypes] : [],
    },
    citizenship: {
      enabled: r.citizenship.enabled,
      regions: r.citizenship.enabled ? [...r.citizenship.regions] : [],
      countries: r.citizenship.enabled ? [...r.citizenship.countries] : [],
      accepted: acceptedCitizenships(r),
    },
    residence: {
      enabled: r.residence.enabled,
      areas: r.residence.enabled ? r.residence.areas.map(publicArea) : [],
      evidence: { ...r.residence.evidence },
      proofOfAddressMaxAgeMonths: r.residence.proofOfAddressMaxAgeMonths,
    },
    age: { minAge: r.age.minAge, maxAge: r.age.maxAge },
    collectLocation: r.collectLocation,
  }
}

/**
 * Join-form fields by level (PLAN v4 §2): the web form shows them, the server re-checks them
 * (`JoinRequestSchema` keeps the identity fields optional; 400 `validation_error` when a required one is missing).
 * Accepts the full requirements or the public projection.
 */
export interface JoinFieldRequirements {
  dateOfBirth: boolean
  nationalId: boolean
  nationality: boolean
  locality: boolean
  /** Browser GPS asked (consent first): required evidence for residence-by-GPS, optional statistics otherwise. */
  gps: 'off' | 'optional'
  phone: 'off' | 'optional' | 'required'
}

export function joinFields(
  r: {
    contact: { phone: 'off' | 'optional' | 'required' }
    identityDocument: { enabled: boolean }
    age: { minAge: number | null; maxAge: number | null }
    residence: { enabled: boolean; evidence: { gps: boolean } }
    collectLocation: boolean
  },
  opts: { hasDistricts: boolean }
): JoinFieldRequirements {
  const document = r.identityDocument.enabled
  return {
    dateOfBirth: document || r.age.minAge != null || r.age.maxAge != null,
    nationalId: document,
    nationality: document,
    locality: opts.hasDistricts,
    gps: r.collectLocation || (r.residence.enabled && r.residence.evidence.gps) ? 'optional' : 'off',
    phone: r.contact.phone,
  }
}

/**
 * What the member does with Didit (the hosted verification), derived from the public projection the same way the
 * server builds the workflow (`effectiveModules().didit`): a selfie (unique human, implied by the document), the
 * identity document, a proof of address. IP analysis never runs alone and asks nothing of the member, so it is not
 * a step. `didit: false` ⇒ no Didit session at all (the join is decided right away).
 */
export interface VerificationSteps {
  didit: boolean
  document: boolean
  selfie: boolean
  proofOfAddress: boolean
}

export function verificationSteps(r: PublicRequirements): VerificationSteps {
  const document = r.identityDocument.enabled
  const selfie = document || r.uniqueHuman
  const proofOfAddress = r.residence.enabled && r.residence.evidence.proofOfAddress
  return { didit: selfie || proofOfAddress, document, selfie, proofOfAddress }
}

/** A voting as listed on the organization landing (drafts excluded). */
export const PublicElectionSummarySchema = z.object({
  id: UuidSchema,
  slug: SlugSchema,
  title: z.string(),
  status: ElectionStatusSchema,
  schedule: ScheduleSchema,
  /** Restricted to these districts (empty = every member). */
  eligibleDistricts: z.array(z.string()),
  /** `${PUBLIC_URL}/o/${orgSlug}/v/${slug}` */
  publicUrl: z.string(),
})
export type PublicElectionSummary = z.infer<typeof PublicElectionSummarySchema>

/* GET /api/public/orgs/:orgSlug → PublicOrganizationResponse (404 for drafts / unknown slugs). */
export const PublicOrganizationSchema = z.object({
  /** Needed client-side: the member auth verifier salt is `davinci-elections:${id}:${email}` (authVerifierSalt). */
  id: UuidSchema,
  slug: SlugSchema,
  name: z.string(),
  description: z.string(),
  organizerName: z.string(),
  /** Public-safe subset of the registration rules. */
  requirements: PublicRequirementsSchema,
  /** Distinct sorted locality names from the districting list (empty = no list, free-text input). */
  localities: z.array(z.string()),
  /** True when the organization has a districting list (the join form must restrict to `localities`). */
  hasDistricts: z.boolean(),
  /** Registration + KYC currently accepted (organization published). */
  registrationOpen: z.boolean(),
  /** Test organization: show the "TEST — no real identity verification" banner and the code hint. */
  testMode: z.boolean(),
  /** Published votings (newest first). Eligibility per member: GET /api/o/:orgSlug/me/elections. */
  elections: z.array(PublicElectionSummarySchema),
  publishedAt: IsoDateTimeSchema.nullable(),
})
export type PublicOrganization = z.infer<typeof PublicOrganizationSchema>

export const PublicOrganizationResponseSchema = z.object({ organization: PublicOrganizationSchema })
export type PublicOrganizationResponse = z.infer<typeof PublicOrganizationResponseSchema>

/* ================================================================== voting */

export const PublicQuestionSchema = z.intersection(
  QuestionInputSchema,
  z.object({
    id: UuidSchema,
    position: z.number().int().min(0),
    process: ProcessStateSchema.pick({
      phase: true,
      processId: true,
      startDate: true,
      endDate: true,
      decryption: true,
    }),
  })
)
export type PublicQuestion = z.infer<typeof PublicQuestionSchema>

/* GET /api/public/orgs/:orgSlug/elections/:electionSlug → PublicElectionResponse (404 for drafts / unknown slugs). */
export const PublicElectionSchema = z.object({
  id: UuidSchema,
  slug: SlugSchema,
  organization: z.object({ id: UuidSchema, slug: SlugSchema, name: z.string() }),
  title: z.string(),
  description: z.string(),
  status: ElectionStatusSchema,
  questions: z.array(PublicQuestionSchema),
  schedule: ScheduleSchema,
  eligibleDistricts: z.array(z.string()),
  /** Residence declaration checkbox on the ballot ("resided at least N days"), null = not asked. */
  residenceDeclarationDays: z.number().int().nullable(),
  /** The organization collects location: GPS at vote time (with consent). */
  collectLocation: z.boolean(),
  /** All questions' processes are open and the sequencer is reachable. */
  votingOpen: z.boolean(),
  /** False ⇒ show "voting temporarily unavailable" banner (D1). */
  sequencerAvailable: z.boolean(),
  /** Present once status is `results` (or partial results exist). */
  results: ResultsResponseSchema.nullable(),
  publishedAt: IsoDateTimeSchema.nullable(),
  /** Test organization: TEST banner. */
  testMode: z.boolean(),
  /** Who unlocks the results: `dkg` (Automatic, the DAVINCI key network) or `council` (an election committee). */
  keyMode: ElectionKeyModeSchema,
  /** Election committee voters are told about (always the real t of n). Null unless keyMode is `council`. */
  committee: z
    .object({
      threshold: z.number().int().min(1).nullable(),
      size: z.number().int().min(1).nullable(),
      policy: CouncilPolicySchema.nullable(),
    })
    .nullable(),
})
export type PublicElection = z.infer<typeof PublicElectionSchema>

export const PublicElectionResponseSchema = z.object({ election: PublicElectionSchema })
export type PublicElectionResponse = z.infer<typeof PublicElectionResponseSchema>

/* ================================================================== runtime config / health */

/**
 * Production-beta disclosure (design §1.5): calm, always visible where a key option is chosen, a "Beta" badge on
 * voting pages, a footnote under results. Disclose, never block.
 */
export const BETA_NOTICE_TEXT =
  "This voting network is in beta and runs on a development security setup. It is great for pilots, communities and internal votes; don't use it yet for legally binding elections. The production security ceremony is coming."
/** One line under a results tally during the beta. */
export const BETA_RESULTS_FOOTNOTE = 'Published during the beta (development security setup).'

const Hex32Schema = z.string().regex(/^0x[0-9a-fA-F]{64}$/)
/** davinci-sdk `DeploymentPins`: overrides of the SDK release pins (all optional). */
export const DeploymentPinsSchema = z.object({
  batchProgramVK: Hex32Schema.optional(),
  resultsProgramVK: Hex32Schema.optional(),
  rootCVadcopFinal: Hex32Schema.optional(),
  ziskVerifierCodeHash: Hex32Schema.optional(),
  ballotVKHash: Hex32Schema.optional(),
})
export type DeploymentPinsConfig = z.infer<typeof DeploymentPinsSchema>

/* GET /api/public/config → PublicConfig (runtime config for the SPA; no secrets). */
export const PublicConfigSchema = z.object({
  /** `${origin}/api/seq` — pass as `sequencerUrls: [sequencerProxyUrl]` to the browser SDK. NEVER change it (revote node). */
  sequencerProxyUrl: z.string(),
  /** Public, CORS-enabled RPCs (PUBLIC_RPC_URLS) the browser SDK uses for registry reads. Never the server's. */
  rpcUrls: z.array(z.string()),
  chainId: z.number().int(),
  /** `sandbox` = every organization uses the Didit sandbox app (dev / E2E deployments). */
  diditMode: DiditModeSchema,
  /** Test organizations can be created (a Didit sandbox key is configured). */
  testModeAvailable: z.boolean(),
  /** SMTP configured (else emails are captured, never delivered). Optional: older servers do not send it. */
  emailConfigured: z.boolean().optional(),
  /** API version (git sha or package version). */
  version: z.string(),
  /** Organizer signup asks for an invitation code (server env ORG_SIGNUP_CODE is set). */
  orgSignupCodeRequired: z.boolean(),
  /** Server quotas the organizer UI should respect (exceeding them ⇒ 429 quota_exceeded). */
  limits: z.object({ maxQuestionsPerElection: z.number().int().min(1) }),
  /**
   * Production-beta disclosure (BETA_NOTICE, default on): shown under the key options, as a badge on voting pages and
   * as a footnote under results. Turned off by configuration once the multiparty setups ship.
   */
  betaNotice: z.object({ enabled: z.boolean(), text: z.string() }),
  /** Council app origin (COUNCIL_APP_URL); null ⇒ committee features hidden (Automatic only). */
  councilAppUrl: z.string().nullable(),
  /**
   * Explicit DAVINCI ProcessRegistry (server env DAVINCI_REGISTRY): the browser SDK must use the same one
   * (`network: { chainId, processRegistry, startBlock, rpcUrls }`). Null ⇒ the SDK default network.
   */
  registry: z.object({ address: EthAddressSchema, startBlock: z.number().int().nullable() }).nullable(),
  /** Explicit release pins (server env DAVINCI_* pins) both SDK instances check the registry against; null ⇒ the SDK's. */
  pins: DeploymentPinsSchema.nullable().optional(),
  /**
   * Key modes the registry has an adapter for (`dkgAdapter()` / `councilAdapter()` non-zero; `council` also needs
   * COUNCIL_APP_URL). Null ⇒ unknown right now (registry unreadable) or an older server: the UI offers its defaults
   * and the server decides.
   */
  keyModes: z.object({ dkg: z.boolean(), council: z.boolean() }).nullable().optional(),
})
export type PublicConfig = z.infer<typeof PublicConfigSchema>

/*
 * GET /api/health → HealthResponse (200 when the DB answers, 503 otherwise). Public answer: `status` + `version` only.
 * The detailed report (everything after `version`) needs `Authorization: Bearer <OPS_TOKEN>` (security fix M12).
 */
export const HealthDetailsSchema = z.object({
  uptimeSeconds: z.number(),
  time: IsoDateTimeSchema,
  db: z.object({ ok: z.boolean(), latencyMs: z.number().nullable(), error: z.string().optional() }),
  sequencer: z.object({ configured: z.boolean(), url: z.string().nullable() }),
  didit: z.object({
    configured: z.boolean(),
    mode: DiditModeSchema,
    /** The sandbox app (test organizations) has a key. */
    sandboxConfigured: z.boolean(),
  }),
  /** SMTP configured (else emails are captured, never delivered). */
  email: z.object({ configured: z.boolean() }),
  /**
   * Chain wallets: the treasury (ORGANIZER_PRIVATE_KEY) that funds the per-organization creator wallets, the
   * balances of the creator wallets with an active voting, and today's (UTC) top-up spend. Null when the chain is not
   * configured or the balances could not be read.
   */
  wallets: z
    .object({
      treasury: z.object({ address: EthAddressSchema, balanceWei: BigIntStringSchema.nullable() }).nullable(),
      creators: z.array(z.object({ address: EthAddressSchema, balanceWei: BigIntStringSchema.nullable() })),
      topupsTodayWei: BigIntStringSchema,
      topupDailyCapWei: BigIntStringSchema,
      topupsPaused: z.boolean(),
    })
    .nullable(),
  /** Election committees: discovery of the registry's Council adapter and manager (fail closed on a mismatch). */
  council: z.object({
    enabled: z.boolean(),
    ok: z.boolean(),
    adapter: EthAddressSchema.nullable(),
    manager: EthAddressSchema.nullable(),
    error: z.string().nullable(),
  }),
  /** Operator alerts raised since boot (creator seed mismatch, top-up caps, stalled results, …), newest first. */
  alerts: z.array(z.object({ code: z.string(), message: z.string(), at: IsoDateTimeSchema, count: z.number() })),
})
export type HealthDetails = z.infer<typeof HealthDetailsSchema>

export const HealthResponseSchema = z
  .object({ status: z.enum(['ok', 'degraded', 'down']), version: z.string() })
  .extend(HealthDetailsSchema.partial().shape)
export type HealthResponse = z.infer<typeof HealthResponseSchema>
